import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Cockpit } from 'components/views/organisms/Cockpit';
import { Vehicle } from 'core/models/vehicle';
import { useVehicle } from 'services/useVehicle';
import { useChannelLayout } from 'components/containers/common/VehicleMonitor/useChannelLayout';
import { CombinedVehicleController } from 'infrastructure/controller/CombinedVehicleController';
import { GamingWheelVehicleController } from 'infrastructure/controller/GamingWheelController';
import { KeyboardVehicleController } from 'infrastructure/controller/keyboardController';
import { useVehicleControllerBind } from 'services/useVehicleControllerBind';
import { useWithVehicleController } from 'services/useVehicleControllerStatus';
import { VideoPlayer } from 'components/containers/common/VehicleMonitor/MultipleVideoPlayer';
import { VehicleStatus } from 'components/containers/common/VehicleMonitor/VehicleStatus';
import { SteeringIndicator } from '../DrivePage/SteeringIndicator';
import { SpeedMeter } from '../DrivePage/SpeedMeter';
import { DriveInfo } from '../DrivePage/DriveInfo';
import { DriveSwitch } from '../DrivePage/DriveSwitch';
import { useMainScreenLayout, useCameraLayout } from 'services/useCameraLayout';
import { Dialog } from 'components/views/atoms/Dialog';
import { CameraLayoutSettingPanel } from 'components/views/organisms/CameraLayoutSettingPanel';
import { AssignedCamera } from 'core/models/layout';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { useSelectedProject } from 'services/useSelectedProject';
import { useEnvironments } from 'services/useEnvironments';
import { useSelectedEnvironment } from 'services/useSelectedEnvironment';
import { useScopeCheck } from 'services/useScope';
import { useChannelStream } from 'services/useChannelStream';
import { useChannelFromTag } from 'services/useChannelFromTag';
import { useSetNavbarVisibility } from 'services/useNavbarVisibility';
import { OperatorCall, useOperatorCallRemoteDevices, useOperatorCall } from 'components/containers/common/OperatorCall';
import { useRecordingToggle } from 'services/useRecordingToggle';
import { Channel } from 'core/models/channel';
import { AdjustedVehicleController } from 'infrastructure/controller/AdjustedVehicleController';
import { useVehicleConfiguration } from 'services/useVehicleConfiguration';
import { DrivingSettingsPanel } from 'components/views/organisms/DrivingSettingsPanel';
import { AlertTicker } from 'components/views/molecules/AlertTicker';
import { useAlertTicker, useWithAlertManager } from 'services/useAlertTicker';
import { useSessionController } from 'services/oidc/useSessionController';
import { useStandBy, useHeartbeatHealth } from 'services/useStandBy';
import { useVehicleStatusError } from 'services/useVehicleStatus';
import { useMediaDeviceStatusError } from 'services/useMediaDeviceStatus';
import { useReloadingStreams } from 'services/useReloadingStreams';
import { useEmergencyButtons } from 'services/useEmergencyButtons';
import { useMediaDeviceRepository } from 'services/useMediaDeviceRepository';
import { MediaDevice } from 'core/models/mediaDevice';
import { Card, FormControlLabel, Grid, Switch } from '@mui/material';
import { ExtraConfig, useSubChannels, useShowAllStreams, useExtraFromSessionStorage, useHeartbeatFrequency, useHeartbeatTimeout, useStreamSelector, useDebugLog, useDevastatorMode } from 'services/useSubChannels';
import { Visualizer } from 'components/containers/common/Visualizer';
import { WallClockWidget } from 'components/views/organisms/WallClockWidget';
import useKeyPressEvent from 'react-use/lib/useKeyPressEvent';
import { LatencyWindow } from 'components/containers/common/LatencyWindow';
import { ignore } from 'utility/ignore';
import { LabeledNumberField } from 'components/views/atoms/LabeledNumberField';
import { AutomaticLatencyMeasureWindow } from './AutomaticLatencyMeasureWindow';

type CameraLayoutDialogProps = {
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
    environmentId: Environment.Id;
    onClose: () => void;
}

const CameraLayoutDialog = (props: CameraLayoutDialogProps) => {
    const { vehicleId, projectId, environmentId, onClose } = props;

    const vehicle = useVehicle(vehicleId, projectId, environmentId);
    const [layout, updateLayout] = useCameraLayout(vehicleId);
    const mediaDeviceRepos = useMediaDeviceRepository();

    const getVideoConfig = useCallback(async (id: MediaDevice.Id) => {
        return await mediaDeviceRepos.getVideoParams(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    const putVideoConfig = useCallback(async (id: MediaDevice.Id, config: Partial<MediaDevice.VideoConfig>) => {
        await mediaDeviceRepos.setVideoParams(id, projectId, environmentId, config);
        await mediaDeviceRepos.restartPublish(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    const getAudioConfig = useCallback(async (id: MediaDevice.Id) => {
        return await mediaDeviceRepos.getAudioParams(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    const putAudioConfig = useCallback(async (id: MediaDevice.Id, config: Partial<MediaDevice.AudioConfig>) => {
        await mediaDeviceRepos.setAudioParams(id, projectId, environmentId, config);
        await mediaDeviceRepos.restartPublish(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    const isUpdateMediaConfigAllowed = useScopeCheck('updateMediaConfigScope') ?? false;

    return (
        <Dialog onClose={onClose}>
            <CameraLayoutSettingPanel
                vehicleId={vehicleId}
                vehicleName={vehicle?.name ?? '??'}
                cameraDevices={vehicle?.cameras}
                microphoneDevices={vehicle?.microphones}
                layout={layout}
                onUpdate={updateLayout}
                getVideoConfig={getVideoConfig}
                putVideoConfig={isUpdateMediaConfigAllowed ? putVideoConfig : undefined}
                getAudioConfig={getAudioConfig}
                putAudioConfig={isUpdateMediaConfigAllowed ? putAudioConfig : undefined}
            />
        </Dialog >
    );
};

type AccelCapSettingDialogProps = {
    vehicleId: Vehicle.Id;
    onClose: () => void;
    extra: ExtraConfig;
    onExtra: (extra: ExtraConfig) => void;
}

function AccelCapSettingDialog(props: AccelCapSettingDialogProps) {
    const { vehicleId, onClose, extra, onExtra } = props;

    const [vehicleConfig, setVehicleConfig] = useVehicleConfiguration(vehicleId);

    const [accelCap, setAccelCap] = useState(1.0);
    useEffect(() => {
        setAccelCap(vehicleConfig?.accelCap ?? 1.0);
    }, [vehicleConfig, setAccelCap]);

    const [accelScale, setAccelScale] = useState(1.0);
    useEffect(() => {
        setAccelScale(vehicleConfig?.accelScale ?? 1.0);
    }, [vehicleConfig, setAccelScale]);

    const [latestExtra, setExtra] = useState<ExtraConfig>(extra ?? {});
    useEffect(() => {
        if (!onExtra || !latestExtra) return;
        onExtra(latestExtra);
    }, [latestExtra, onExtra]);

    const numberOfSubChannels = useSubChannels(latestExtra, setExtra);
    const isShowAllStreams = useShowAllStreams(latestExtra, setExtra);
    const heartbeatFrequency = useHeartbeatFrequency(latestExtra, setExtra);
    const heartbeatTimeout = useHeartbeatTimeout(latestExtra, setExtra);
    const streamSelector = useStreamSelector(latestExtra, setExtra);
    const enableDebugLog = useDebugLog(latestExtra, setExtra);
    const isDevastatorMode = useDevastatorMode(latestExtra, setExtra);

    return (
        <Dialog onClose={() => {
            setVehicleConfig({ accelCap, accelScale });
            onClose();
        }}>
            <DrivingSettingsPanel
                accelCap={accelCap}
                onAccelCap={setAccelCap}
                accelScale={accelScale}
                onAccelScale={setAccelScale}
            />
            <Card>
                <Grid container direction='column'>
                    <Grid>
                        <LabeledNumberField
                            value={heartbeatFrequency}
                            onChange={
                                e => {
                                    if (e !== undefined && e > 0) {
                                        setExtra(prev => ({ ...prev, heartbeatFrequency: e }));
                                    }
                                }
                            }
                            disabled={false}
                            label="Heartbeat Rate [Hz]"
                        />
                    </Grid>
                    <Grid>
                        <LabeledNumberField
                            value={heartbeatTimeout}
                            onChange={
                                e => {
                                    if (e !== undefined && e > 0) {
                                        setExtra(prev => ({ ...prev, heartbeatTimeout: e }));
                                    }
                                }
                            }
                            disabled={false}
                            label="Heartbeat Timeout [ms]"
                        />
                    </Grid>
                </Grid>
            </Card>
            <Card>
                <Grid container direction='column'>
                    <Grid>
                        <LabeledNumberField
                            value={numberOfSubChannels}
                            onChange={
                                e => {
                                    setExtra(prev => ({ ...prev, subChannels: e }));
                                }
                            }
                            disabled={false}
                            label="Number sub-channels"
                        />
                    </Grid>
                    <Grid>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isShowAllStreams}
                                    onChange={e => {
                                        const isEnabled = e.target.checked;
                                        setExtra(prev => ({ ...prev, showAllStreams: isEnabled }));
                                    }} />
                            }
                            label="Enable show all streams (experimental)" />
                    </Grid>
                    <Grid>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={streamSelector === 'v2'}
                                    onChange={e => {
                                        const isV2 = e.target.checked;
                                        setExtra(prev => ({ ...prev, streamSelector: isV2 ? 'v2' : 'v1' }));
                                    }} />
                            }
                            label="Use stream selector v2 (experimental)" />
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={streamSelector !== 'v2'}
                                    checked={enableDebugLog}
                                    onChange={e => {
                                        const isEnabled = e.target.checked;
                                        setExtra(prev => ({ ...prev, debugLog: isEnabled }));
                                    }} />
                            }
                            label="Enable debug log (experimental)" />
                    </Grid>
                    <Grid>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isDevastatorMode}
                                    onChange={e => {
                                        const isEnabled = e.target.checked;
                                        setExtra(prev => ({ ...prev, devastatorMode: isEnabled }));
                                    }} />
                            }
                            label="Enable devastator mode (dangerous!!)" />
                    </Grid>
                </Grid>
            </Card>
        </Dialog>
    );
}

function useConnectedChannels() {
    const [connectedChannels, setConnectedChannels] = useState<Channel[]>([]);
    const onChannelConnect = useCallback((channel: Channel, prepared: boolean) => {
        setConnectedChannels(channels => {
            const knownChannels = new Set(channels);
            if (prepared) {
                knownChannels.add(channel);
            } else {
                knownChannels.delete(channel);
            }
            return [...knownChannels.values()];
        });
    }, [setConnectedChannels]);

    return [connectedChannels, onChannelConnect] as const;
}

export type CockpitViewProps = {
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
    environmentId: Environment.Id;
}

export const CockpitView = (props: CockpitViewProps) => {
    const { vehicleId, projectId, environmentId } = props;

    const { extra, setExtra } = useExtraFromSessionStorage();

    const isSubChannelsEnabled = useSubChannels(extra, setExtra);
    const isShowAllStreams = useShowAllStreams(extra, setExtra);
    const heartbeatFrequency = useHeartbeatFrequency(extra, setExtra);
    const heartbeatTimeout = useHeartbeatTimeout(extra, setExtra);
    const streamSelector = useStreamSelector(extra, setExtra);
    const enableDebugLog = useDebugLog(extra, setExtra);
    const isDevastatorMode = useDevastatorMode(extra, setExtra);

    const channelLayout = useChannelLayout(vehicleId, projectId, environmentId, isSubChannelsEnabled);

    const [connectedChannels, onChannelConnect] = useConnectedChannels();

    const [isRecording, onRecordingClick_] = useRecordingToggle(connectedChannels);
    const onRecordingClick = isDevastatorMode ? onRecordingClick_ : undefined;

    const [isAccelSettingVisible, setAccelSettingVisible] = useState(false);

    const getElementForAnchor = useCallback((camera: AssignedCamera, stretch = false) => {
        const channel = camera.tag && channelLayout
            ? channelLayout.cameras.get(camera.tag)
            : undefined;
        const mirror = camera.mirror ?? false;
        const upsidedown = camera.upsidedown ?? false;
        const audio = false;
        return channel
            ? <VideoPlayer channel={channel} mirror={mirror} upsidedown={upsidedown} audio={audio} viewOnly={true} stretch={stretch} preserveAspectRatio={!stretch} projectId={projectId} environmentId={environmentId} onPrepare={prepared => onChannelConnect(channel, prepared)} showAllStreams={isShowAllStreams} streamSelector={streamSelector} enableDebugLog={enableDebugLog} />
            : <div />;
    }, [channelLayout, projectId, environmentId, isShowAllStreams, streamSelector, enableDebugLog, onChannelConnect]);

    const [displayLayout] = useMainScreenLayout(vehicleId);

    const layout = useMemo(() => {
        const centerIndex = Math.floor(displayLayout.center.length / 2);
        const centerCamera = displayLayout.center.slice(centerIndex, centerIndex + 1);
        const topCameras = displayLayout.top;
        const leftCameras = [...displayLayout.center.slice(0, centerIndex), ...displayLayout.left];
        const rightCameras = [...displayLayout.center.slice(centerIndex + 1), ...displayLayout.right];

        return {
            center: centerCamera,
            top: topCameras,
            left: leftCameras,
            right: rightCameras,
        };
    }, [displayLayout]);

    const microphoneChannel = useChannelFromTag(displayLayout.microphone?.tag, channelLayout);
    const microphoneStream = useChannelStream(microphoneChannel, true);

    const [settingDialogVisible, setSettingDialogVisible] = useState(false);

    const { toggleState: onTelephoneClick, ...telephoneProps } = useOperatorCall();
    const { telephones, toggleCallsState } = useOperatorCallRemoteDevices(vehicleId, projectId, environmentId,);

    const { alerting, messages: alertMessages, addAlertMessage } = useAlertTicker();
    const sessionController = useSessionController();
    useEffect(() => {
        const listener = () => {
            addAlertMessage('Your access token has been invalidated. Please sign-in again!!');
        };
        sessionController.addEventListener('revoke', listener);
        return () => sessionController.removeEventListener('revoke', listener);
    }, [sessionController, addAlertMessage]);

    const [standby, setStandBy] = useStandBy(vehicleId, projectId, heartbeatFrequency);
    const onStandBy = useCallback(() => {
        setStandBy(prev => !prev);
    }, [setStandBy]);

    const heartbeatStatus = useHeartbeatHealth(vehicleId, projectId, standby, heartbeatFrequency, heartbeatTimeout);
    const vehicleIds = useMemo(() => [vehicleId], [vehicleId]);
    const emergencyButtonsError = useEmergencyButtons(projectId, vehicleIds);
    useEffect(() => {
        if (!emergencyButtonsError) return;
        if (emergencyButtonsError instanceof TypeError) {
            console.info('No UI server found.');
        } else {
            console.error(emergencyButtonsError);
        }
    }, [emergencyButtonsError]);

    const vehicleStatusError = useVehicleStatusError();
    useEffect(() => {
        if (vehicleStatusError) {
            addAlertMessage(vehicleStatusError.message);
        }
    }, [vehicleStatusError, addAlertMessage]);

    const mediaDeviceStatusError = useMediaDeviceStatusError();
    useEffect(() => {
        if (mediaDeviceStatusError) {
            addAlertMessage(mediaDeviceStatusError.message);
        }
    }, [mediaDeviceStatusError, addAlertMessage]);

    const [isReloading, onReloadClick] = useReloadingStreams(channelLayout, projectId, environmentId);

    const isControlAllowed = useScopeCheck('sendVehicleCommandScope') ?? false;
    const isEmergencyAllowed = useScopeCheck('sendEmergencyStopCommandScope') ?? false;

    const [clockVisible, showClock_] = useState(false);
    const showClock = isDevastatorMode ? showClock_ : () => 0;
    useKeyPressEvent('c', null, () => showClock(prev => !prev));

    const [latencyVisible, showLatency_] = useState(false);
    const showLatency = isDevastatorMode ? showLatency_ : () => 0;
    useKeyPressEvent('l', null, () => showLatency(prev => !prev));

    const [measurementVisible, showMeasurement_] = useState(false);
    const showMeasurement = isDevastatorMode ? showMeasurement_ : () => 0;
    useKeyPressEvent('m', null, () => showMeasurement(prev => !prev));

    return (
        projectId
            ? <React.Fragment>
                <Cockpit
                    layout={layout}
                    getElementForAnchor={getElementForAnchor}
                    microphoneStream={microphoneStream}
                    vehicleStatus={<VehicleStatus vehicleId={vehicleId} projectId={projectId} environmentId={environmentId} darkMode={true} />}
                    steeringIndicator={<SteeringIndicator vehicleId={vehicleId} projectId={projectId} />}
                    speedMeter={<SpeedMeter vehicleId={vehicleId} projectId={projectId} />}
                    driveInfo={<DriveInfo vehicleId={vehicleId} projectId={projectId} />}
                    driveSwitch={<DriveSwitch vehicleId={vehicleId} projectId={projectId} />}
                    onSettingsClick={() => setSettingDialogVisible(!settingDialogVisible)}
                    isReloading={isReloading}
                    onReloadClick={onReloadClick}
                    telephone={telephoneProps.state}
                    telephones={telephones}
                    onTelephoneClick={onTelephoneClick}
                    onTelephonesClick={toggleCallsState}
                    isRecording={isRecording}
                    onRecordingClick={onRecordingClick}
                    onAccelSettingClick={() => setAccelSettingVisible(true)}
                    alertTicker={<AlertTicker caption='Stop the vehicle immediately! You have several critical problems!' messages={alertMessages} />}
                    alerting={alerting}
                    standby={standby}
                    onStandBy={onStandBy}
                    heartbeatStatus={heartbeatStatus}
                    isStandByAllowed={isControlAllowed || isEmergencyAllowed}
                    widgets={isDevastatorMode ? [
                        <Visualizer key='viz' vehicleId={vehicleId} projectId={projectId} />,
                    ] : []}
                />
                {Object.keys(telephones).length > 0 && <OperatorCall vehicleId={vehicleId} projectId={projectId} environmentId={environmentId} {...telephoneProps} />}
                {
                    settingDialogVisible
                        ? <CameraLayoutDialog vehicleId={vehicleId} projectId={projectId} environmentId={environmentId} onClose={() => setSettingDialogVisible(false)} />
                        : <React.Fragment />
                }
                {
                    isAccelSettingVisible
                        ? <AccelCapSettingDialog vehicleId={vehicleId} onClose={() => setAccelSettingVisible(false)}
                            extra={extra}
                            onExtra={setExtra} />
                        : <></>
                }
                {clockVisible ? <WallClockWidget /> : <></>}
                {latencyVisible ? <LatencyWindow layout={channelLayout} onClose={() => showLatency(false)} /> : <></>}
                {measurementVisible ? <AutomaticLatencyMeasureWindow layout={channelLayout} onClose={() => showMeasurement_(false)} /> : <></>}
            </React.Fragment>
            : <p>Please select project</p>
    );
};

export type CockpitPageWithProjectProps = {
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
    environmentId: Environment.Id;
}

const CockpitPageWithProject = (props: CockpitPageWithProjectProps) => {
    const { vehicleId, projectId, environmentId } = props;

    const showNavbar = useSetNavbarVisibility();
    useEffect(() => {
        showNavbar(false);
        return () => showNavbar(true);
    }, [showNavbar]);

    const [vehicleConfig] = useVehicleConfiguration(vehicleId);

    const controller = useMemo(() => {
        return new AdjustedVehicleController(vehicleConfig, new CombinedVehicleController([
            new GamingWheelVehicleController(),
            new KeyboardVehicleController(),
        ]));
    }, [vehicleConfig]);

    useVehicleControllerBind(vehicleId, projectId, environmentId, controller);

    const withVehicleController = useWithVehicleController();

    return withVehicleController(controller, vehicleId)(
        <CockpitView vehicleId={vehicleId} projectId={projectId} environmentId={environmentId} />
    );
};

export type CockpitPageProps = {}

export function CockpitPage(props: CockpitPageProps) {
    ignore(props);

    const withAlertManager = useWithAlertManager();

    const { vehicleId } = useParams<{ vehicleId: string }>();
    if (vehicleId === undefined) throw Error('missing vehicle ID in URL');

    const projectId = useSelectedProject()?.id;
    const environments = useEnvironments(projectId);
    const environmentId = useSelectedEnvironment(environments)?.id;
    if (!projectId || !environmentId) {
        return <p>Please select project and environment</p>;
    }

    return withAlertManager(
        <CockpitPageWithProject vehicleId={vehicleId} projectId={projectId} environmentId={environmentId} />
    );
}
